import React, { useState, useCallback, useEffect } from 'react';
import { X, RotateCcw, ChevronDown, Loader2, ShieldCheck } from 'lucide-react';
import { useAssumptionDraftStore } from '../../store/useAssumptionDraftStore';
import { useWhatIfScenario } from '../../hooks/useWhatIfScenario';
import { CapExEditor } from './CapExEditor';
import type { PropertyAnalysisResult, WhatIfOverrides } from '../../types/calculations';
import { formatCurrency } from '../../utils/formatters';

type DrawerTab = 'edit' | 'ssot';

interface SSOTAssumptions {
  effectiveTaxRate: number;
  bonusDepreciationRate: number;
  permanentLoanRate: number;
  constructionLoanRate: number;
  constructionLoanPoints: number;
  constructionPeriodMonths: number;
  tier1OpExRate: number;
  tier2OpExRate: number;
  tier3OpExRate: number;
  tier1CapExPerUnit: number;
  tier2CapExPerUnit: number;
  tier3CapExPerUnit: number;
  exitCapRule: string;
  rentGrowthRate: number;
  noiGrowthRate: number;
}

interface AssumptionsDrawerProps {
  data: PropertyAnalysisResult;
  propertyId: string;
  onRecalculated: (result: PropertyAnalysisResult) => void;
  ssotAssumptions?: SSOTAssumptions;
  yardiComparison?: {
    ssotRentGrowth: number;
    yardiRentGrowth: number;
    variance: number;
    assessment: 'CONSERVATIVE' | 'ALIGNED' | 'AGGRESSIVE';
    source: string;
  };
}

/* ---------- Slider input row ---------- */

interface SliderRowProps {
  label: string;
  value: number | null;
  baselineValue: number;
  min: number;
  max: number;
  step: number;
  format: (v: number) => string;
  onChange: (v: number) => void;
  onReset: () => void;
}

const SliderRow: React.FC<SliderRowProps> = ({
  label, value, baselineValue, min, max, step, format, onChange, onReset,
}) => {
  const current = value ?? baselineValue;
  const isChanged = value !== null && value !== baselineValue;

  return (
    <div className="space-y-1.5">
      <div className="flex items-center justify-between">
        <span className="text-sm text-cream">{label}</span>
        <div className="flex items-center gap-2">
          <span className={`text-sm font-mono font-medium ${isChanged ? 'text-amber-600' : 'text-ivory'}`}>
            {format(current)}
          </span>
          {isChanged && (
            <button
              onClick={onReset}
              className="w-5 h-5 flex items-center justify-center text-cream/40 hover:text-gold transition-colors rounded-full hover:bg-gold/10"
              title={`Reset to ${format(baselineValue)}`}
            >
              <RotateCcw size={12} />
            </button>
          )}
        </div>
      </div>
      <input
        type="range"
        min={min}
        max={max}
        step={step}
        value={current}
        onChange={(e) => onChange(parseFloat(e.target.value))}
        className="w-full h-1.5 rounded-full appearance-none cursor-pointer
          bg-slate-dark
          [&::-webkit-slider-thumb]:appearance-none
          [&::-webkit-slider-thumb]:w-4
          [&::-webkit-slider-thumb]:h-4
          [&::-webkit-slider-thumb]:rounded-full
          [&::-webkit-slider-thumb]:bg-gold
          [&::-webkit-slider-thumb]:border-2
          [&::-webkit-slider-thumb]:border-charcoal
          [&::-webkit-slider-thumb]:shadow-md
          [&::-webkit-slider-thumb]:cursor-pointer
          [&::-moz-range-thumb]:w-4
          [&::-moz-range-thumb]:h-4
          [&::-moz-range-thumb]:rounded-full
          [&::-moz-range-thumb]:bg-gold
          [&::-moz-range-thumb]:border-2
          [&::-moz-range-thumb]:border-charcoal
          [&::-moz-range-thumb]:cursor-pointer"
      />
    </div>
  );
};

/* ---------- Collapsible drawer section ---------- */

interface DrawerSectionProps {
  title: string;
  defaultOpen?: boolean;
  changedCount?: number;
  children: React.ReactNode;
}

const DrawerSection: React.FC<DrawerSectionProps> = ({ title, defaultOpen = true, changedCount = 0, children }) => {
  const [isOpen, setIsOpen] = useState(defaultOpen);

  return (
    <div className="border-b border-slate-dark last:border-b-0">
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className="w-full flex items-center justify-between py-3 px-1 cursor-pointer text-left"
      >
        <div className="flex items-center gap-2">
          <h4 className="text-sm font-semibold tracking-widest text-gold uppercase">{title}</h4>
          {changedCount > 0 && (
            <span className="text-sm bg-amber-100 text-amber-700 border border-amber-300 px-1.5 py-0.5 rounded-full font-medium">
              {changedCount} modified
            </span>
          )}
        </div>
        <ChevronDown
          className={`w-4 h-4 text-cream/40 transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`}
        />
      </button>
      {isOpen && (
        <div className="pb-4 px-1 space-y-4">
          {children}
        </div>
      )}
    </div>
  );
};

/* ---------- Formatting helpers ---------- */

const fmtPct = (v: number) => `${(v * 100).toFixed(2)}%`;
const fmtPctHalf = (v: number) => `${(v * 100).toFixed(1)}%`;
const fmtMo = (v: number) => `${Math.round(v)} mo`;
const fmtDollar = (v: number) => formatCurrency(v) ?? '$0';

/* ---------- Main Drawer ---------- */

/* ---------- SSOT Reference Panel (read-only) ---------- */

const SSOTReferencePanel: React.FC<{ assumptions?: SSOTAssumptions; yardiComparison?: AssumptionsDrawerProps['yardiComparison'] }> = ({
  assumptions, yardiComparison,
}) => {
  if (!assumptions) {
    return <div className="text-sm text-cream/50 px-1 py-4">SSOT data not available</div>;
  }

  const rows: Array<{ section: string; items: Array<{ label: string; value: string }> }> = [
    {
      section: 'Tax & Depreciation',
      items: [
        { label: 'Effective Tax Rate', value: `${(assumptions.effectiveTaxRate * 100).toFixed(1)}%` },
        { label: 'Bonus Depreciation', value: `${(assumptions.bonusDepreciationRate * 100).toFixed(0)}%` },
      ],
    },
    {
      section: 'Loan Rates',
      items: [
        { label: 'Permanent Rate', value: `${(assumptions.permanentLoanRate * 100).toFixed(2)}%` },
        { label: 'Construction Rate', value: `${(assumptions.constructionLoanRate * 100).toFixed(2)}%` },
        { label: 'Origination Points', value: `${(assumptions.constructionLoanPoints * 100).toFixed(2)}%` },
        { label: 'Construction Period', value: `${assumptions.constructionPeriodMonths} months` },
      ],
    },
    {
      section: 'OpEx by Tier',
      items: [
        { label: 'Tier 1', value: `${(assumptions.tier1OpExRate * 100).toFixed(1)}%` },
        { label: 'Tier 2', value: `${(assumptions.tier2OpExRate * 100).toFixed(1)}%` },
        { label: 'Tier 3', value: `${(assumptions.tier3OpExRate * 100).toFixed(1)}%` },
      ],
    },
    {
      section: 'CapEx by Tier',
      items: [
        { label: 'Tier 1', value: formatCurrency(assumptions.tier1CapExPerUnit) + '/unit' },
        { label: 'Tier 2', value: formatCurrency(assumptions.tier2CapExPerUnit) + '/unit' },
        { label: 'Tier 3', value: formatCurrency(assumptions.tier3CapExPerUnit) + '/unit' },
      ],
    },
    {
      section: 'Exit & Growth',
      items: [
        { label: 'Exit Cap Rule', value: assumptions.exitCapRule },
        { label: 'Rent Growth', value: `${(assumptions.rentGrowthRate * 100).toFixed(2)}%` },
        { label: 'NOI Growth', value: `${(assumptions.noiGrowthRate * 100).toFixed(2)}%` },
      ],
    },
  ];

  return (
    <div className="space-y-4">
      {rows.map(section => (
        <div key={section.section}>
          <h4 className="text-sm font-semibold tracking-widest text-gold uppercase mb-2">{section.section}</h4>
          <div className="space-y-1">
            {section.items.map(item => (
              <div key={item.label} className="flex items-center justify-between py-1.5 px-2 rounded bg-obsidian/40">
                <span className="text-sm text-cream/70">{item.label}</span>
                <div className="flex items-center gap-1.5">
                  <span className="text-sm bg-gold/10 text-gold border border-gold/20 px-1.5 py-0.5 rounded-full font-medium">SSOT</span>
                  <span className="text-sm font-mono text-ivory">{item.value}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      ))}

      {yardiComparison && (
        <div>
          <h4 className="text-sm font-semibold tracking-widest text-gold uppercase mb-2">Market Benchmark</h4>
          <div className="space-y-1">
            <div className="flex items-center justify-between py-1.5 px-2 rounded bg-obsidian/40">
              <span className="text-sm text-cream/70">SSOT Rent Growth</span>
              <span className="text-sm font-mono text-ivory">{(yardiComparison.ssotRentGrowth * 100).toFixed(2)}%</span>
            </div>
            <div className="flex items-center justify-between py-1.5 px-2 rounded bg-obsidian/40">
              <span className="text-sm text-cream/70">Yardi Rent Growth</span>
              <span className="text-sm font-mono text-ivory">{(yardiComparison.yardiRentGrowth * 100).toFixed(2)}%</span>
            </div>
            <div className="flex items-center justify-between py-1.5 px-2 rounded bg-obsidian/40">
              <span className="text-sm text-cream/70">Assessment</span>
              <span className={`text-sm font-bold ${
                yardiComparison.assessment === 'CONSERVATIVE' ? 'text-emerald' :
                yardiComparison.assessment === 'AGGRESSIVE' ? 'text-crimson' : 'text-amber-400'
              }`}>{yardiComparison.assessment}</span>
            </div>
          </div>
          <p className="text-sm text-cream/30 mt-2">Source: {yardiComparison.source}</p>
        </div>
      )}
    </div>
  );
};

export const AssumptionsDrawer: React.FC<AssumptionsDrawerProps> = ({
  data, propertyId, onRecalculated, ssotAssumptions, yardiComparison,
}) => {
  const {
    draft, baseline, isOpen, isModified,
    closeDrawer, setField, resetField, resetAll, getChangedFields, getOverrides,
  } = useAssumptionDraftStore();

  const [drawerTab, setDrawerTab] = useState<DrawerTab>('edit');
  const mutation = useWhatIfScenario(propertyId);

  // Close on Escape key
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen) closeDrawer();
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [isOpen, closeDrawer]);

  const handleRecalculate = useCallback(() => {
    const overrides = getOverrides();
    if (Object.keys(overrides).length === 0) return;

    const payload: { overrides: WhatIfOverrides; hold_period_years: number } = {
      overrides: {
        ...overrides,
        source: 'user' as const,
      } as WhatIfOverrides,
      hold_period_years: data?.inputs?.hold_period_years || 10,
    };

    mutation.mutate(payload, {
      onSuccess: (response) => {
        onRecalculated(response.result);
      },
    });
  }, [getOverrides, data, mutation, onRecalculated]);

  if (!isOpen || !baseline) return null;

  const changedFields = getChangedFields();

  // Count changes per section
  const financingFields = ['interestRate', 'constructionLoanRate', 'constructionLoanPoints', 'constructionPeriodMonths'];
  const rentFields = ['rentPerUnit', 'vacancyRate'];
  const growthFields = ['rentGrowth', 'expenseGrowthRate'];
  const opexFields = ['opexRate'];
  const capexFields = ['capexItems'];

  const countSection = (fields: string[]) => changedFields.filter(f => fields.includes(f)).length;

  return (
    <>
      {/* Backdrop overlay */}
      <div
        className="fixed inset-0 bg-black/30 z-40 transition-opacity"
        onClick={closeDrawer}
      />

      {/* Drawer panel */}
      <div
        className="fixed inset-y-0 right-0 z-50 w-full max-w-[480px] bg-charcoal border-l border-slate-dark shadow-2xl flex flex-col transform transition-transform duration-300 ease-out"
      >
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-dark flex-shrink-0">
          <div>
            <h3 className="font-serif text-gold text-lg">Assumptions</h3>
            <p className="text-sm text-cream/50 mt-0.5">
              {drawerTab === 'edit' ? 'Adjust inputs and recalculate returns' : 'SSOT config.py reference values'}
            </p>
          </div>
          <button
            onClick={closeDrawer}
            className="w-8 h-8 flex items-center justify-center text-cream/40 hover:text-ivory transition-colors rounded-lg hover:bg-obsidian"
          >
            <X size={20} />
          </button>
        </div>

        {/* Tab navigation */}
        <div className="flex gap-1 px-6 pt-3 pb-1">
          <button
            onClick={() => setDrawerTab('edit')}
            className={`flex-1 px-3 py-2 text-sm font-medium rounded-md transition-all ${
              drawerTab === 'edit'
                ? 'bg-gold/20 text-gold border border-gold/30'
                : 'text-stone-500 hover:text-ivory hover:bg-charcoal/60'
            }`}
          >
            Edit Assumptions
          </button>
          <button
            onClick={() => setDrawerTab('ssot')}
            className={`flex-1 flex items-center justify-center gap-1.5 px-3 py-2 text-sm font-medium rounded-md transition-all ${
              drawerTab === 'ssot'
                ? 'bg-gold/20 text-gold border border-gold/30'
                : 'text-stone-500 hover:text-ivory hover:bg-charcoal/60'
            }`}
          >
            <ShieldCheck size={14} />
            SSOT Reference
          </button>
        </div>

        {/* Scrollable content */}
        <div className="flex-1 overflow-y-auto px-6 py-4">

          {/* SSOT Reference tab */}
          {drawerTab === 'ssot' && (
            <SSOTReferencePanel assumptions={ssotAssumptions} yardiComparison={yardiComparison} />
          )}

          {/* Edit Assumptions tab */}
          {drawerTab === 'edit' && <>

          {/* Section 1: Financing */}
          <DrawerSection title="Financing" changedCount={countSection(financingFields)}>
            <SliderRow
              label="Permanent Loan Rate"
              value={draft.interestRate}
              baselineValue={baseline.interestRate}
              min={0.03} max={0.15} step={0.0025}
              format={fmtPct}
              onChange={(v) => setField('interestRate', v)}
              onReset={() => resetField('interestRate')}
            />
            <SliderRow
              label="Construction Loan Rate"
              value={draft.constructionLoanRate}
              baselineValue={baseline.constructionLoanRate}
              min={0.03} max={0.18} step={0.0025}
              format={fmtPct}
              onChange={(v) => setField('constructionLoanRate', v)}
              onReset={() => resetField('constructionLoanRate')}
            />
            <SliderRow
              label="Origination Points"
              value={draft.constructionLoanPoints}
              baselineValue={baseline.constructionLoanPoints}
              min={0} max={0.05} step={0.0025}
              format={fmtPct}
              onChange={(v) => setField('constructionLoanPoints', v)}
              onReset={() => resetField('constructionLoanPoints')}
            />
            <SliderRow
              label="Construction Period"
              value={draft.constructionPeriodMonths}
              baselineValue={baseline.constructionPeriodMonths}
              min={3} max={24} step={1}
              format={fmtMo}
              onChange={(v) => setField('constructionPeriodMonths', Math.round(v))}
              onReset={() => resetField('constructionPeriodMonths')}
            />
          </DrawerSection>

          {/* Section 2: Rent Assumptions */}
          <DrawerSection title="Rent Assumptions" changedCount={countSection(rentFields)}>
            <SliderRow
              label="As-Is Rent / Unit"
              value={draft.rentPerUnit}
              baselineValue={baseline.rentPerUnit}
              min={Math.max(200, Math.round(baseline.rentPerUnit * 0.5))}
              max={Math.round(baseline.rentPerUnit * 2)}
              step={25}
              format={fmtDollar}
              onChange={(v) => setField('rentPerUnit', Math.round(v))}
              onReset={() => resetField('rentPerUnit')}
            />
            <SliderRow
              label="Vacancy Rate"
              value={draft.vacancyRate}
              baselineValue={baseline.vacancyRate}
              min={0} max={0.30} step={0.005}
              format={fmtPctHalf}
              onChange={(v) => setField('vacancyRate', v)}
              onReset={() => resetField('vacancyRate')}
            />
          </DrawerSection>

          {/* Section 3: Growth Rates */}
          <DrawerSection title="Growth Rates" changedCount={countSection(growthFields)}>
            <SliderRow
              label="Rent Growth"
              value={draft.rentGrowth}
              baselineValue={baseline.rentGrowth}
              min={0} max={0.10} step={0.0025}
              format={fmtPct}
              onChange={(v) => setField('rentGrowth', v)}
              onReset={() => resetField('rentGrowth')}
            />
            <SliderRow
              label="Expense Growth"
              value={draft.expenseGrowthRate}
              baselineValue={baseline.expenseGrowthRate}
              min={0} max={0.10} step={0.0025}
              format={fmtPct}
              onChange={(v) => setField('expenseGrowthRate', v)}
              onReset={() => resetField('expenseGrowthRate')}
            />
          </DrawerSection>

          {/* Section 4: Operating Expenses */}
          <DrawerSection title="Operating Expenses" changedCount={countSection(opexFields)}>
            <SliderRow
              label="OpEx Rate"
              value={draft.opexRate}
              baselineValue={baseline.opexRate}
              min={0.20} max={0.60} step={0.005}
              format={fmtPctHalf}
              onChange={(v) => setField('opexRate', v)}
              onReset={() => resetField('opexRate')}
            />
          </DrawerSection>

          {/* Section 5: Capital Expenditures */}
          <DrawerSection title="Capital Expenditures" defaultOpen={false} changedCount={countSection(capexFields)}>
            <CapExEditor
              totalUnits={baseline.totalUnits}
              ssotCapexTotal={(data?.capex_analysis as any)?.total_budget}
            />
          </DrawerSection>

          </>}
        </div>

        {/* Footer (edit tab only) */}
        {drawerTab === 'edit' && (
          <>
            <div className="flex items-center justify-between px-6 py-4 border-t border-slate-dark flex-shrink-0 bg-charcoal">
              <div className="flex items-center gap-3">
                {changedFields.length > 0 && (
                  <span className="text-sm text-amber-600 font-medium">
                    {changedFields.length} assumption{changedFields.length !== 1 ? 's' : ''} changed
                  </span>
                )}
                {isModified && (
                  <button
                    onClick={resetAll}
                    className="text-sm text-cream/50 hover:text-ivory transition-colors underline"
                  >
                    Reset All
                  </button>
                )}
              </div>
              <button
                onClick={handleRecalculate}
                disabled={!isModified || mutation.isPending}
                className={`flex items-center gap-2 px-5 py-2 rounded-lg text-sm font-semibold transition-all ${
                  isModified && !mutation.isPending
                    ? 'bg-gold text-charcoal hover:bg-gold-bright shadow-md'
                    : 'bg-slate-dark text-cream/30 cursor-not-allowed'
                }`}
              >
                {mutation.isPending ? (
                  <>
                    <Loader2 size={16} className="animate-spin" />
                    Recalculating...
                  </>
                ) : (
                  'Recalculate'
                )}
              </button>
            </div>

            {mutation.isError && (
              <div className="px-6 py-2 bg-crimson/10 border-t border-crimson/30 text-crimson text-sm">
                Recalculation failed: {(mutation.error as Error)?.message || 'Unknown error'}
              </div>
            )}
          </>
        )}
      </div>
    </>
  );
};
